import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { API, useAuth, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Clock, CheckCircle, Package, Truck, MapPin, ChevronDown, ChevronUp, Search, SlidersHorizontal, Calendar, X, Phone, ExternalLink, FileText, Download, Camera, UserCheck, User as UserIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { aggregateTaxBreakdown } from "../../utils/taxBreakdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrders() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const [riderDialog, setRiderDialog] = useState(null); // {orderId}
  const [readyImageDialog, setReadyImageDialog] = useState(null); // {orderId}
  const [deliverDialog, setDeliverDialog] = useState(null); // {orderId}
  const [cancelDialog, setCancelDialog] = useState(null); // {orderId}
  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [trackingLink, setTrackingLink] = useState("");
  const [readyImageFile, setReadyImageFile] = useState(null);
  const [readyImagePreview, setReadyImagePreview] = useState(null);
  const [deliverCode, setDeliverCode] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [kitchenFilter, setKitchenFilter] = useState("all");
  // Per-kitchen unread badge counts. Derived from the SAME localStorage list
  // used by the AdminLayout bell (`sm_admin_notifs_<user_id>`), so a kitchen
  // chip badge appears even if the order arrived BEFORE this page loaded
  // (the bell's 30s polling tick fills that store regardless of which admin
  // page you were viewing). Cleared when admin clicks the kitchen chip.
  const NOTIFS_KEY = `sm_admin_notifs_${user?.user_id || "anon"}`;
  const readUnreadFromBell = () => {
    try {
      const raw = localStorage.getItem(NOTIFS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return list.reduce((acc, n) => {
        if (n.read) return acc;
        const k = n.kitchen_code || "__unassigned__";
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});
    } catch { return {}; }
  };
  const [kitchenUnread, setKitchenUnread] = useState(readUnreadFromBell);
  // Re-read every 5s so badges reflect what the bell's 30s polling tick has captured,
  // and on cross-tab `storage` events, and on `focus` so a returning admin sees fresh counts.
  useEffect(() => {
    const refresh = () => setKitchenUnread(readUnreadFromBell());
    const id = setInterval(refresh, 5000);
    const onStorage = (e) => { if (e.key === NOTIFS_KEY) refresh(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const markKitchenRead = (kitchenKey) => {
    try {
      const raw = localStorage.getItem(NOTIFS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      let changed = false;
      const next = list.map((n) => {
        const k = n.kitchen_code || "__unassigned__";
        const match = kitchenKey === "__all__" ? true : k === kitchenKey;
        if (match && !n.read) { changed = true; return { ...n, read: true }; }
        return n;
      });
      if (changed) localStorage.setItem(NOTIFS_KEY, JSON.stringify(next));
    } catch { /* ignore */ }
    setKitchenUnread(readUnreadFromBell());
  };
  const [viewReadyImage, setViewReadyImage] = useState(null); // image URL string
  const [viewRiderInfo, setViewRiderInfo] = useState(null); // rider object
  const wsRef = useRef(null);

  // Filter state
  const [filters, setFilters] = useState({
    status: "all",
    orderNumber: "",
    customerName: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    fetchOrders();
    
    if (user) {
      const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
      wsRef.current = new WebSocket(`${wsUrl}/ws/admin_${user.user_id}`);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "new_order") {
          setOrders((prev) => [data.order, ...prev]);
          toast.success("New order received!");
          // Refresh the badge counts (AdminLayout will have just appended this
          // order to the shared notifications list — re-read it).
          setTimeout(() => setKitchenUnread(readUnreadFromBell()), 200);
        }
      };
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [user]);

  // When arriving with ?highlight=<order_id>, expand + scroll to + flash that card
  useEffect(() => {
    const id = searchParams.get("highlight");
    if (!id || orders.length === 0) return;
    const exists = orders.some((o) => o.order_id === id);
    if (!exists) return;
    setExpandedOrder(id);
    setHighlightedOrderId(id);
    setTimeout(() => {
      const el = document.getElementById(`order-card-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    const t = setTimeout(() => setHighlightedOrderId(null), 3500);
    // Clear the query param so the highlight doesn't re-fire on subsequent state changes
    searchParams.delete("highlight");
    setSearchParams(searchParams, { replace: true });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/orders/admin`, { credentials: "include" });
      if (response.ok) setOrders(await response.json());
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering
  const filteredOrders = orders.filter((order) => {
    // Universal search across order number, name, phone, email
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        (order.order_number || "").toLowerCase().includes(q) ||
        order.order_id.toLowerCase().includes(q) ||
        (order.user_name || "").toLowerCase().includes(q) ||
        (order.user_email || "").toLowerCase().includes(q) ||
        (order.user_phone || "").includes(searchQuery);
      if (!matchesSearch) return false;
    }
    if (filters.status !== "all" && order.status !== filters.status) return false;
    if (filters.orderNumber && !order.order_id.toLowerCase().includes(filters.orderNumber.toLowerCase()) && !(order.order_number || "").toLowerCase().includes(filters.orderNumber.toLowerCase())) return false;
    if (filters.customerName && !order.user_name.toLowerCase().includes(filters.customerName.toLowerCase()) && !order.user_email.toLowerCase().includes(filters.customerName.toLowerCase())) return false;
    if (filters.dateFrom) {
      const orderDate = new Date(order.created_at).setHours(0, 0, 0, 0);
      const fromDate = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      if (orderDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const orderDate = new Date(order.created_at).setHours(23, 59, 59, 999);
      const toDate = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      if (orderDate > toDate) return false;
    }
    if (kitchenFilter !== "all") {
      const k = order.kitchen_code || "__unassigned__";
      if (kitchenFilter === "__unassigned__" ? !!order.kitchen_code : k !== kitchenFilter) return false;
    }
    return true;
  });

  // Pre-compute kitchen counts (respect every other filter except the kitchen one)
  const kitchenCounts = orders.reduce((acc, order) => {
    // re-apply non-kitchen filters
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches =
        (order.order_number || "").toLowerCase().includes(q) ||
        order.order_id.toLowerCase().includes(q) ||
        (order.user_name || "").toLowerCase().includes(q) ||
        (order.user_email || "").toLowerCase().includes(q) ||
        (order.user_phone || "").includes(searchQuery);
      if (!matches) return acc;
    }
    if (filters.status !== "all" && order.status !== filters.status) return acc;
    if (filters.dateFrom) {
      const od = new Date(order.created_at).setHours(0, 0, 0, 0);
      const fd = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      if (od < fd) return acc;
    }
    if (filters.dateTo) {
      const od = new Date(order.created_at).setHours(23, 59, 59, 999);
      const td = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      if (od > td) return acc;
    }
    const key = order.kitchen_code || "__unassigned__";
    acc[key] = (acc[key] || 0) + 1;
    acc.__total__ = (acc.__total__ || 0) + 1;
    return acc;
  }, {});

  const updateOrderStatus = async (orderId, newStatus, extra = {}) => {
    try {
      const response = await fetch(`${API}/orders/${orderId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus, ...extra }),
      });

      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || "Failed to update status");
        return;
      }

      const updated = await response.json();
      setOrders((prev) =>
        prev.map((order) =>
          order.order_id === orderId ? { ...updated } : order
        )
      );

      toast.success(`Order status updated to ${formatStatus(newStatus)}`);
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (newStatus === "out_for_delivery") {
      const order = orders.find(o => o.order_id === orderId);
      const kitchenId = order?.kitchen_id || "";
      try {
        const res = await fetch(`${API}/riders${kitchenId ? `?kitchen_id=${kitchenId}` : ""}`, { credentials: "include" });
        if (res.ok) setRiders(await res.json());
        else setRiders([]);
      } catch { setRiders([]); }
      setSelectedRider(null);
      setTrackingLink("");
      setRiderDialog({ orderId });
      return;
    }
    if (newStatus === "pick_up") {
      // No rider needed for pickup, just advance the status directly
      updateOrderStatus(orderId, newStatus);
      return;
    }
    if (newStatus === "ready") {
      setReadyImageFile(null);
      setReadyImagePreview(null);
      setReadyImageDialog({ orderId });
      return;
    }
    if (newStatus === "delivered") {
      setDeliverCode("");
      setDeliverDialog({ orderId });
      return;
    }
    updateOrderStatus(orderId, newStatus);
  };

  const handleCancelRefund = async (orderId) => {
    setCancelReason("");
    setCancelDialog({ orderId });
  };

  const confirmCancelRefund = async () => {
    setDialogLoading(true);
    try {
      const res = await fetch(`${API}/orders/${cancelDialog.orderId}/cancel-refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: cancelReason }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Failed to cancel");
        setDialogLoading(false);
        return;
      }
      toast.success("Order cancelled. Refund request sent to admin.");
      fetchOrders();
    } catch {
      toast.error("Failed to cancel order");
    } finally {
      setDialogLoading(false);
      setCancelDialog(null);
    }
  };

  const confirmOutForDelivery = async () => {
    if (!selectedRider) { toast.error("Please select a rider"); return; }
    setDialogLoading(true);
    await updateOrderStatus(riderDialog.orderId, "out_for_delivery", {
      rider_id: selectedRider.user_id,
      rider_name: selectedRider.name,
      rider_phone: selectedRider.phone || "",
      tracking_link: trackingLink,
    });
    setDialogLoading(false);
    setRiderDialog(null);
  };

  const confirmReady = async () => {
    setDialogLoading(true);
    let imageUrl = "";
    if (readyImageFile) {
      try {
        const formData = new FormData();
        formData.append("file", readyImageFile);
        const res = await fetch(`${API}/orders/${readyImageDialog.orderId}/ready-image`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          imageUrl = data.url;
        }
      } catch {}
    }
    await updateOrderStatus(readyImageDialog.orderId, "ready", { ready_image_url: imageUrl });
    setDialogLoading(false);
    setReadyImageDialog(null);
  };

  const handleReadyImageCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReadyImageFile(file);
      setReadyImagePreview(URL.createObjectURL(file));
    }
  };

  const confirmDeliver = async () => {
    if (!deliverCode || deliverCode.length !== 6) { toast.error("Enter a valid 6-digit delivery code"); return; }
    setDialogLoading(true);
    await updateOrderStatus(deliverDialog.orderId, "delivered", { delivery_code: deliverCode });
    setDialogLoading(false);
    setDeliverDialog(null);
    setDeliverCode("");
  };

  const formatStatus = (status) => {
    const labels = {
      received: "Received",
      preparing: "In Process",
      ready: "Ready",
      out_for_delivery: "Out for Delivery",
      pick_up: "Pick Up",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      received: "bg-yellow-100 text-yellow-700",
      preparing: "bg-blue-100 text-blue-700",
      ready: "bg-green-100 text-green-700",
      out_for_delivery: "bg-purple-100 text-purple-700",
      pick_up: "bg-amber-100 text-amber-700",
      delivered: "bg-gray-100 text-gray-600",
      cancelled: "bg-red-100 text-red-600",
    };
    return colors[status] || "bg-gray-100 text-gray-600";
  };

  const getStatusIcon = (status) => {
    const icons = {
      received: Clock,
      preparing: Clock,
      ready: CheckCircle,
      out_for_delivery: Truck,
      pick_up: Package,
      delivered: MapPin,
      cancelled: Clock,
    };
    const Icon = icons[status] || Clock;
    return <Icon size={14} />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusOptionsDelivery = ["received", "preparing", "ready", "out_for_delivery", "delivered"];
  const statusOptionsPickup = ["received", "preparing", "ready", "pick_up", "delivered"];

  // Get next valid statuses (forward-only) based on order type
  const getNextStatuses = (currentStatus, orderType) => {
    const options = orderType === "pickup" ? statusOptionsPickup : statusOptionsDelivery;
    const idx = options.indexOf(currentStatus);
    if (idx === -1 || currentStatus === "delivered" || currentStatus === "cancelled") return [];
    return options.slice(idx + 1);
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === "status") return v !== "all";
    return v !== "";
  }).length + (searchQuery ? 1 : 0);

  const clearFilters = () => {
    setSearchQuery("");
    setFilters({ status: "all", orderNumber: "", customerName: "", dateFrom: "", dateTo: "" });
    setKitchenFilter("all");
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-full overflow-x-hidden" data-testid="admin-orders">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Order Management</h1>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/orders/export-pdf${filters.status !== "all" ? `?status=${filters.status}` : ""}${filters.dateFrom ? `${filters.status !== "all" ? "&" : "?"}date_from=${filters.dateFrom}` : ""}${filters.dateTo ? `${(filters.status !== "all" || filters.dateFrom) ? "&" : "?"}date_to=${filters.dateTo}` : ""}`, "_blank")}
              className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-2 sm:px-3"
              data-testid="export-orders-pdf"
            >
              <FileText size={13} className="mr-1" /> PDF
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/orders/export-excel${filters.status !== "all" ? `?status=${filters.status}` : ""}${filters.dateFrom ? `${filters.status !== "all" ? "&" : "?"}date_from=${filters.dateFrom}` : ""}${filters.dateTo ? `${(filters.status !== "all" || filters.dateFrom) ? "&" : "?"}date_to=${filters.dateTo}` : ""}`, "_blank")}
              className="text-green-600 border-green-200 hover:bg-green-50 text-xs px-2 sm:px-3"
              data-testid="export-orders-excel"
            >
              <Download size={13} className="mr-1" /> Excel
            </Button>
            <span className="text-xs text-gray-500">
              {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Advanced Filter Bar */}
        <div className="mb-4 sm:mb-6 space-y-3" data-testid="orders-filter-bar">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative flex-1 min-w-0 basis-full sm:basis-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search order #, customer name, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-white w-full"
                data-testid="orders-search-input"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full sm:w-44 flex-1 sm:flex-none min-w-0" data-testid="orders-filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="preparing">In Process</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                <SelectItem value="pick_up">Pick Up</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`gap-2 ${activeFilterCount > 0 ? "border-green-500 text-green-600" : ""}`}
              data-testid="orders-filter-toggle"
            >
              <SlidersHorizontal size={16} />
              More
              {activeFilterCount > 1 && (
                <span className="bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500" data-testid="orders-filter-clear">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 animate-fade-in">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Customer Name / Email</Label>
                <Input
                  placeholder="Search customer..."
                  value={filters.customerName}
                  onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                  className="bg-white h-9 text-sm"
                  data-testid="orders-filter-customer"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Date From</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="bg-white h-9 text-sm"
                  data-testid="orders-filter-date-from"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Date To</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="bg-white h-9 text-sm"
                  data-testid="orders-filter-date-to"
                />
              </div>
            </div>
          )}
        </div>

        {/* Kitchen filter chips */}
        {(() => {
          const totalAcrossKitchens = kitchenCounts.__total__ || 0;
          const kitchenKeys = Object.keys(kitchenCounts).filter((k) => k !== "__total__");
          // Hide chip row entirely if there's only one kitchen represented (no value)
          if (kitchenKeys.length <= 1 && !kitchenKeys.includes("__unassigned__")) return null;
          const sortedKeys = kitchenKeys.sort((a, b) => a === "__unassigned__" ? 1 : b === "__unassigned__" ? -1 : a.localeCompare(b));
          return (
            <div className="mb-4 sm:mb-6 flex items-center gap-2 overflow-x-auto pt-2 pb-2" data-testid="orders-kitchen-chips">
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold flex-shrink-0">Kitchen:</span>
              <button
                type="button"
                onClick={() => { setKitchenFilter("all"); markKitchenRead("__all__"); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  kitchenFilter === "all"
                    ? "bg-green-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700"
                }`}
                data-testid="kitchen-chip-all"
              >
                All ({totalAcrossKitchens})
              </button>
              {sortedKeys.map((key) => {
                const label = key === "__unassigned__" ? "Unassigned" : key;
                const unread = kitchenUnread[key] || 0;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setKitchenFilter(key); markKitchenRead(key); }}
                    className={`relative px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                      kitchenFilter === key
                        ? "bg-orange-600 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-700"
                    }`}
                    data-testid={`kitchen-chip-${key}`}
                  >
                    {label} ({kitchenCounts[key]})
                    {unread > 0 && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                        data-testid={`kitchen-chip-badge-${key}`}
                      >
                        {unread > 9 ? "9+" : unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-32 w-full rounded-xl"></div>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No orders found</p>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" className="mt-2 text-green-600" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Group orders by kitchen */}
            {(() => {
              const grouped = {};
              filteredOrders.forEach((order) => {
                const key = order.kitchen_code || "Unassigned";
                if (!grouped[key]) grouped[key] = { orders: [], city: order.kitchen_city || "", locality: order.kitchen_locality || "" };
                grouped[key].orders.push(order);
              });
              const sortedKeys = Object.keys(grouped).sort((a, b) => a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b));
              return sortedKeys.map((kitchenKey) => (
                <div key={kitchenKey}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className="text-sm font-bold text-orange-700 bg-orange-50 px-3 py-1 rounded-full" data-testid={`kitchen-group-${kitchenKey}`}>{kitchenKey}</span>
                    {grouped[kitchenKey].city && <span className="text-[10px] text-gray-500">{grouped[kitchenKey].city}{grouped[kitchenKey].locality ? ` - ${grouped[kitchenKey].locality}` : ""}</span>}
                    <span className="text-[10px] text-gray-500 ml-auto">{grouped[kitchenKey].orders.length} order{grouped[kitchenKey].orders.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-4">
            {grouped[kitchenKey].orders.map((order) => (
              <div
                key={order.order_id}
                id={`order-card-${order.order_id}`}
                className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                  highlightedOrderId === order.order_id
                    ? "border-green-500 ring-2 ring-green-200 shadow-lg"
                    : "border-gray-100"
                }`}
                data-testid={`order-card-${order.order_id}`}
              >
                {/* Order Header */}
                <div
                  className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)
                  }
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{order.user_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {formatStatus(order.status)}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs sm:text-sm">
                        {order.items.length} item{order.items.length > 1 ? "s" : ""} · {symbol}{Number(order.total_amount || 0).toFixed(2)}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-gray-400 text-[10px] sm:text-xs">{formatDate(order.created_at)}</p>
                        {order.order_number && (
                          <span className="text-green-600 text-[10px] font-semibold bg-green-50 px-1.5 py-0.5 rounded" data-testid={`order-number-${order.order_id}`}>{order.order_number}</span>
                        )}
                        {order.kitchen_code && (
                          <span className="text-orange-600 text-[10px] font-semibold bg-orange-50 px-1.5 py-0.5 rounded" data-testid={`order-kitchen-${order.order_id}`}>
                            {order.kitchen_code}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center flex-shrink-0">
                      {expandedOrder === order.order_id ? (
                        <ChevronUp size={18} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={18} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  {/* Action buttons — below the header on mobile, inline on desktop */}
                  {order.status !== "delivered" && order.status !== "cancelled" && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      <Select value="" onValueChange={(value) => handleStatusChange(order.order_id, value)}>
                        <SelectTrigger className="w-full sm:w-40 h-8 text-xs" data-testid={`status-select-${order.order_id}`}>
                          <SelectValue placeholder="Next Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {getNextStatuses(order.status, order.order_type).map((status) => (
                            <SelectItem key={status} value={status}>{formatStatus(status)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => handleCancelRefund(order.order_id)}
                        className="px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex-shrink-0"
                        data-testid={`cancel-order-${order.order_id}`}
                      >
                        Cancel
                      </button>
                      {order.ready_image_url && (
                        <button
                          onClick={() => setViewReadyImage(`${BACKEND_URL}${order.ready_image_url}`)}
                          className="text-[10px] font-semibold text-green-600 flex items-center gap-1 flex-shrink-0"
                          data-testid={`view-ready-photo-${order.order_id}`}
                        >
                          <Camera size={11} /> Photo
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {expandedOrder === order.order_id && (
                  <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50">
                    <div className="space-y-3 sm:space-y-4">
                      {/* Items */}
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Order Items</p>
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  {item.name} x{item.quantity}
                                </span>
                                <span className="text-gray-900 font-medium">
                                  {symbol}{Number(item.subtotal || item.base_price * item.quantity || 0).toFixed(2)}
                                </span>
                              </div>
                              {item.selected_variations?.length > 0 && (
                                <p className="text-[10px] text-gray-500 ml-2">
                                  {item.selected_variations.map(v => `${v.variation_name}: ${v.option_value}`).join(", ")}
                                </p>
                              )}
                              {item.selected_addons?.length > 0 && (
                                <p className="text-[10px] text-gray-500 ml-2">
                                  + {item.selected_addons.map(a => a.name).join(", ")}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Bill Summary — matches checkout & order tracking format */}
                        <div className="mt-2 sm:mt-3 pt-2 border-t border-gray-200 space-y-1 text-xs sm:text-sm">
                          {(() => {
                            const sub = Number(order.subtotal || 0);
                            const disc = Number(order.discount_amount || 0);
                            const tax = Number(order.total_tax || 0);
                            const total = Number(order.total_amount || 0);
                            const roundOff = total - (sub - disc + tax);
                            const breakdown = aggregateTaxBreakdown(order.items || []);
                            return (
                              <>
                                <div className="flex justify-between"><span className="text-gray-500">Base Amount</span><span className="text-gray-700" data-testid={`admin-base-${order.order_id}`}>{symbol}{sub.toFixed(2)}</span></div>
                                {disc > 0 && (
                                  <div className="flex justify-between"><span className="text-green-600 font-medium">{order.discount_label || "App Only Discount"}</span><span className="text-green-600 font-semibold" data-testid={`admin-discount-${order.order_id}`}>-{symbol}{disc.toFixed(2)}</span></div>
                                )}
                                {breakdown.length > 0 ? (
                                  breakdown.map((tx) => tx.amount > 0 && (
                                    <div key={`${tx.name}-${tx.percentage}`} className="flex justify-between" data-testid={`admin-tax-${order.order_id}-${tx.name}`}>
                                      <span className="text-gray-500">{tx.name} {tx.percentage}%</span>
                                      <span className="text-gray-700">{symbol}{Number(tx.amount).toFixed(2)}</span>
                                    </div>
                                  ))
                                ) : (
                                  tax > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-500">Tax</span><span className="text-gray-700" data-testid={`admin-tax-${order.order_id}`}>{symbol}{tax.toFixed(2)}</span></div>
                                  )
                                )}
                                {Math.abs(roundOff) >= 0.01 && (
                                  <div className="flex justify-between"><span className="text-gray-400">Round Off</span><span className="text-gray-500" data-testid={`admin-rounding-${order.order_id}`}>{roundOff > 0 ? "+" : "-"}{symbol}{Math.abs(roundOff).toFixed(2)}</span></div>
                                )}
                                <div className="flex justify-between font-bold pt-1 border-t border-gray-200"><span className="text-gray-900">Total</span><span className="text-green-600" data-testid={`admin-total-${order.order_id}`}>{symbol}{total.toFixed(2)}</span></div>
                                {order.payment_method && (
                                  <div className="flex justify-between text-[10px]"><span className="text-gray-400">Paid via</span><span className="text-gray-500 font-medium capitalize">{order.payment_method === "gateway" ? "Online Payment" : "Wallet"}</span></div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Delivery Info with Google Maps */}
                      <div>
                        <p className="font-medium text-gray-700 mb-2">
                          {order.order_type === "pickup" ? "Pickup Address" : "Delivery Address"}
                          {order.order_type === "pickup" && (
                            <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold" data-testid={`pickup-badge-${order.order_id}`}>Pickup Order</span>
                          )}
                        </p>
                        <div className="flex items-start gap-2">
                          <MapPin size={16} className="text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-gray-600 text-sm">{order.delivery_address}</p>
                            <a
                              href={order.delivery_lat && order.delivery_lng
                                ? `https://www.google.com/maps?q=${order.delivery_lat},${order.delivery_lng}`
                                : `https://www.google.com/maps/search/${encodeURIComponent(order.delivery_address)}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 text-xs font-semibold flex items-center gap-1 mt-1 hover:underline"
                              data-testid={`admin-maps-link-${order.order_id}`}
                            >
                              <ExternalLink size={10} /> Open in Google Maps
                            </a>
                            {order.delivery_instructions && (
                              <p className="text-gray-400 text-xs mt-1">
                                {order.order_type === "pickup" ? "Note" : "Delivery Note"}: {order.delivery_instructions}
                              </p>
                            )}
                          </div>
                        </div>
                        {order.billing_address && (
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <p className="text-[10px] text-gray-400 font-medium mb-1">Billing Address</p>
                            <p className="text-gray-600 text-sm">{order.billing_address}</p>
                          </div>
                        )}
                      </div>

                      {/* Order Notes */}
                      {order.order_notes && (
                        <div>
                          <p className="font-medium text-gray-700 mb-1">Order Notes</p>
                          <p className="text-gray-600 text-sm bg-yellow-50 p-2 rounded">{order.order_notes}</p>
                        </div>
                      )}

                      {/* Status Timeline - Visual Tracker */}
                      <div>
                        <p className="font-medium text-gray-700 mb-3">Status Timeline</p>
                        <AdminStatusTracker
                          order={order}
                          onViewReadyImage={(url) => setViewReadyImage(`${BACKEND_URL}${url}`)}
                          onViewRiderInfo={(rider) => setViewRiderInfo(rider)}
                        />
                      </div>

                      {/* Contact */}
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Customer</p>
                        <p className="text-gray-600 text-sm">{order.user_name}</p>
                        <p className="text-gray-600 text-sm">{order.user_email}</p>
                        {order.user_phone && <p className="text-gray-600 text-sm flex items-center gap-1"><Phone size={12} /> {order.user_phone}</p>}
                      </div>

                      {/* Download Invoice */}
                      <div>
                        <a
                          href={`${BACKEND_URL}/api/orders/${order.order_id}/invoice`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white px-4 py-2 text-sm font-semibold transition-colors"
                          data-testid={`admin-download-invoice-${order.order_id}`}
                        >
                          <FileText size={14} /> Download Invoice PDF
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Rider Assignment Dialog */}
      <Dialog open={!!riderDialog} onOpenChange={() => setRiderDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck size={18} className="text-blue-600" /> Assign Delivery Partner</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-500">Select a rider for this delivery</p>
            {riders.length === 0 ? (
              <p className="text-sm text-red-500 text-center py-4">No riders available. Promote a user to Rider role first.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {riders.map((rider) => (
                  <button
                    key={rider.user_id}
                    onClick={() => setSelectedRider(rider)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${selectedRider?.user_id === rider.user_id ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}
                    data-testid={`rider-option-${rider.user_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">{(rider.name || "R")[0]}</div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{rider.name}</p>
                      <p className="text-[10px] text-gray-400">{rider.phone || rider.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div>
              <label className="text-[10px] text-gray-400 font-medium">Tracking Link (optional)</label>
              <Input value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="Paste Google Maps / tracking URL..." className="mt-1" data-testid="tracking-link-input" />
            </div>
            <Button onClick={confirmOutForDelivery} disabled={!selectedRider || dialogLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-rider-btn">
              {dialogLoading ? "Assigning..." : "Assign & Mark Out for Delivery"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ready Image Dialog */}
      <Dialog open={!!readyImageDialog} onOpenChange={() => setReadyImageDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera size={18} className="text-green-600" /> Capture Ready Image</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-500">Take a photo of the order before marking as ready</p>
            {readyImagePreview ? (
              <div className="relative">
                <img src={readyImagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                <button onClick={() => { setReadyImageFile(null); setReadyImagePreview(null); }} className="absolute top-2 right-2 bg-white rounded-full p-1 shadow"><X size={14} /></button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" data-testid="capture-image-label">
                <Camera size={32} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Tap to capture image</span>
                <input type="file" accept="image/*" capture="environment" onChange={handleReadyImageCapture} className="hidden" data-testid="capture-image-input" />
              </label>
            )}
            <Button onClick={confirmReady} disabled={dialogLoading} className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="confirm-ready-btn">
              {dialogLoading ? "Processing..." : readyImageFile ? "Upload & Mark Ready" : "Mark Ready (No Image)"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Ready Image Popup */}
      {viewReadyImage && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewReadyImage(null)} data-testid="ready-image-popup">
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewReadyImage(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg z-10" data-testid="close-ready-image-popup">
              <X size={16} className="text-gray-600" />
            </button>
            <img src={viewReadyImage} alt="Order ready" className="w-full rounded-xl shadow-2xl" data-testid="ready-image-popup-img" />
            <p className="text-center text-white text-xs mt-3 font-medium">Order Ready Photo</p>
          </div>
        </div>
      )}
      {/* Rider Info Popup */}
      {viewRiderInfo && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewRiderInfo(null)} data-testid="rider-info-popup">
          <div className="relative bg-white rounded-2xl p-5 max-w-xs w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewRiderInfo(null)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" data-testid="close-rider-info-popup">
              <X size={16} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Truck size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Delivery Partner</p>
                <p className="text-sm font-bold text-gray-900">{viewRiderInfo.rider_name || "Assigned Rider"}</p>
              </div>
            </div>
            <div className="space-y-3">
              <a href={viewRiderInfo.rider_phone ? `tel:${viewRiderInfo.rider_phone}` : undefined} className={`flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl ${viewRiderInfo.rider_phone ? "hover:bg-blue-50 transition-colors" : ""}`} data-testid="rider-popup-call">
                <Phone size={15} className="text-blue-500" />
                <span className="text-sm font-medium text-blue-600">{viewRiderInfo.rider_phone || "Phone not available"}</span>
              </a>
              {viewRiderInfo.tracking_link && (
                <a href={viewRiderInfo.tracking_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl hover:bg-green-50 transition-colors" data-testid="rider-popup-track">
                  <MapPin size={15} className="text-green-500" />
                  <span className="text-sm font-medium text-green-600">Track Live Location</span>
                  <ExternalLink size={11} className="text-green-400 ml-auto" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Deliver Code Dialog */}
      <Dialog open={!!deliverDialog} onOpenChange={() => setDeliverDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle size={18} className="text-green-600" /> Enter Delivery Code</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-500">Enter the customer's 6-digit code to confirm handover.</p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-[10px] text-gray-500">Order: <span className="font-semibold text-gray-700">{(() => { const o = orders.find(o => o.order_id === deliverDialog?.orderId); return o?.order_number || deliverDialog?.orderId; })()}</span></p>
              <p className="text-sm font-medium text-gray-900" data-testid="deliver-dialog-customer-name">{(() => { const o = orders.find(o => o.order_id === deliverDialog?.orderId); return o?.user_name || ""; })()}</p>
              {(() => { const o = orders.find(o => o.order_id === deliverDialog?.orderId); return o?.user_phone ? <p className="text-xs text-blue-600" data-testid="deliver-dialog-customer-phone"><Phone size={11} className="inline mr-1" />{o.user_phone}</p> : null; })()}
            </div>
            <Input
              value={deliverCode}
              onChange={(e) => setDeliverCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="text-center text-2xl font-mono tracking-[0.3em] h-14 text-green-400 bg-gray-900 border-gray-700 placeholder:text-gray-600"
              maxLength={6}
              data-testid="admin-delivery-code-input"
            />
            <Button onClick={confirmDeliver} disabled={!deliverCode || deliverCode.length !== 6 || dialogLoading} className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="admin-confirm-deliver-btn">
              {dialogLoading ? "Processing..." : "Confirm Delivery"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel & Refund Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><X size={18} /> Cancel Order & Request Refund</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-500">This will cancel the order and create a refund request for admin approval.</p>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Reason for cancellation</Label>
              <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Enter reason..." data-testid="cancel-reason-input" />
            </div>
            <Button onClick={confirmCancelRefund} disabled={dialogLoading} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
              {dialogLoading ? "Processing..." : "Cancel & Request Refund"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </AdminLayout>
  );
}

const adminStatusStepsDelivery = [
  { key: "received", label: "Received", icon: CheckCircle, desc: "Order confirmed" },
  { key: "preparing", label: "In Process", icon: Clock, desc: "Getting things ready" },
  { key: "ready", label: "Ready", icon: Package, desc: "Ready for pickup" },
  { key: "out_for_delivery", label: "On the Way", icon: Truck, desc: "Out for delivery" },
  { key: "delivered", label: "Delivered", icon: MapPin, desc: "You've made our day!" },
];

const adminStatusStepsPickup = [
  { key: "received", label: "Received", icon: CheckCircle, desc: "Order confirmed" },
  { key: "preparing", label: "In Process", icon: Clock, desc: "Getting things ready" },
  { key: "ready", label: "Ready", icon: Package, desc: "Ready for collection" },
  { key: "pick_up", label: "Pick Up", icon: Package, desc: "Customer collecting order" },
  { key: "delivered", label: "Delivered", icon: CheckCircle, desc: "Order picked up!" },
];

function AdminStatusTracker({ order, onViewReadyImage, onViewRiderInfo }) {
  const steps = order.order_type === "pickup" ? adminStatusStepsPickup : adminStatusStepsDelivery;
  const statusKeys = steps.map((s) => s.key);
  const currentIdx = statusKeys.indexOf(order.status);

  const getTimestamp = (key) => {
    const entry = order.status_history?.find((h) => h.status === key);
    if (!entry) return null;
    return {
      time: new Date(entry.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
      updatedBy: entry.updated_by,
    };
  };

  return (
    <div className="status-tracker" data-testid="admin-status-tracker">
      {steps.map((step, index) => {
        const isCompleted = index < currentIdx;
        const isActive = index === currentIdx;
        const isPending = index > currentIdx;
        const Icon = step.icon;
        const ts = getTimestamp(step.key);

        return (
          <div key={step.key} className={`status-step ${isCompleted ? "completed" : ""} ${isActive ? "active" : ""}`} data-testid={`admin-status-${step.key}`}>
            <div className="status-dot"><Icon size={10} /></div>
            <div>
              <p className={`text-sm ${isPending ? "text-gray-300" : "text-gray-900 font-semibold"}`}>{step.label}</p>
              <p className={`text-xs ${isPending ? "text-gray-500" : "text-gray-500"}`}>{step.desc}</p>
              {!isPending && ts && (
                <p className="text-[10px] text-green-600 font-medium mt-0.5">
                  {ts.time}
                  {ts.updatedBy && <span className="text-blue-500 ml-1">by {ts.updatedBy}</span>}
                </p>
              )}
              {step.key === "ready" && !isPending && order.ready_image_url && (
                <button
                  onClick={() => onViewReadyImage(order.ready_image_url)}
                  className="flex items-center gap-1 mt-1 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors"
                  data-testid={`admin-timeline-ready-photo-${order.order_id}`}
                >
                  <Camera size={11} /> View Photo
                </button>
              )}
              {step.key === "out_for_delivery" && !isPending && order.rider && (
                <button
                  onClick={() => onViewRiderInfo(order.rider)}
                  className="flex items-center gap-1 mt-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  data-testid={`admin-timeline-rider-info-${order.order_id}`}
                >
                  <UserIcon size={11} /> Rider Info
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
